"use client";

import React, { useState, useCallback, useMemo } from "react";
import { 
  SimpleModal, 
  useGlobalModalStack, 
  MODAL_IDS 
} from "@west-tennessee-consulting/ui";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { ClientSelectWithModal } from "@/components/ui/selects/components/ClientSelectWithModal";
import type { SelectOption } from "@/components/ui/responsive-select";
import { useEnhancedTimeline } from "@/lib/hooks/api/timeline/enhanced-timeline";
import { useOfferingHooks } from "@/lib/hooks/api/offerings/use-offerings-hooks";
import { extractTimelineNotesForAi } from "@/lib/core/ai/extract-timeline-notes-for-ai";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { toast } from "sonner";
import { MemoizedMarkdown } from "@west-tennessee-consulting/meridian/components/MemoizedMarkdown";
import { Sparkles, FileSearch, Send, BrainCircuit } from "lucide-react";

export function SolutionEngineeringModal() {
  const { isModalOpen, closeModal, getModalData } = useGlobalModalStack();
  const isOpen = isModalOpen(MODAL_IDS.offerings.solutionEngineering);
  const modalData = getModalData(MODAL_IDS.offerings.solutionEngineering);
  
  const [painPoints, setPainPoints] = useState("");
  const [selectedContactId, setSelectedContactId] = useState<string>(modalData?.contactId || "");
  const [customerEmail, setCustomerEmail] = useState<string>(modalData?.customerEmail || "");
  const [isGenerating, setIsGenerating] = useState(false);
  const [result, setResult] = useState<string | null>(null);

  const offeringsHooks = useOfferingHooks();
  const { data: offeringsData, isLoading: offeringsLoading } = offeringsHooks.usePaginated({ first: 100 });

  // Fetch timeline if we have an email
  const { data: timelineData, isLoading: timelineLoading, refetch: refetchTimeline } = useEnhancedTimeline(
    customerEmail,
    { enabled: !!customerEmail, limit: 50 }
  );

  const handleContactChange = useCallback((id: string, contact?: SelectOption) => {
    setSelectedContactId(id);
    setCustomerEmail(contact?.email ?? "");
    setResult(null);
  }, []);

  const extractFromNotes = useCallback(async () => {
    if (!timelineData || timelineData.length === 0) {
      toast.error("No discovery notes found for this customer");
      return;
    }

    try {
      // Hydrate S3 bodies + strip HTML/reply chains into short plaintext excerpts.
      const notes = await extractTimelineNotesForAi(timelineData);
      if (!notes.trim()) {
        toast.error("No usable note text found for this customer");
        return;
      }
      setPainPoints((prev) =>
        prev ? `${prev}\n\n--- Extracted from Notes ---\n${notes}` : notes
      );
      toast.success("Notes extracted successfully");
    } catch {
      toast.error("Failed to extract notes from the timeline");
    }
  }, [timelineData]);

  const generateBlueprint = async () => {
    if (!painPoints.trim()) {
      toast.error("Please provide client pain points");
      return;
    }

    if (!offeringsData || offeringsData.data.length === 0) {
      toast.error("No offerings available to map");
      return;
    }

    setIsGenerating(true);
    setResult(null);

    try {
      const response = await fetch("/api/offerings/solution-engineering", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          painPoints,
          offerings: offeringsData.data.map(o => ({
            id: o.id,
            title: o.title,
            description: o.description,
          })),
        }),
      });

      if (!response.ok) {
        const error = (await response.json()) as { error?: string };
        throw new Error(error.error || "Failed to generate blueprint");
      }

      const data = (await response.json()) as { result: string };
      setResult(data.result);
      toast.success("Solution blueprint generated!");
    } catch (error: unknown) {
      toast.error(
        error instanceof Error ? error.message : "Failed to generate blueprint"
      );
    } finally {
      setIsGenerating(false);
    }
  };

  const handleClose = () => {
    closeModal(MODAL_IDS.offerings.solutionEngineering);
  };

  return (
    <SimpleModal
      opened={isOpen}
      onClose={handleClose}
      title={
        <div className="flex items-center gap-2">
          <BrainCircuit className="h-5 w-5 text-primary" />
          <span>Solution Engineering Architect</span>
        </div>
      }
      size="xl"
    >
      <div className="space-y-6 max-h-[80vh] overflow-y-auto pr-2">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Select Customer (Optional)</Label>
              <ClientSelectWithModal
                value={selectedContactId}
                onValueChange={handleContactChange}
                placeholder="Search for a customer..."
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="pain-points">Client Pain Points / Discovery Context</Label>
                {customerEmail && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={extractFromNotes}
                    disabled={timelineLoading || !timelineData}
                    className="h-7 text-xs text-primary hover:text-primary hover:bg-primary/10"
                  >
                    {timelineLoading ? (
                      <LoadingSpinner className="h-3 w-3 mr-1" />
                    ) : (
                      <FileSearch className="h-3 w-3 mr-1" />
                    )}
                    Extract from Notes
                  </Button>
                )}
              </div>
              <Textarea
                id="pain-points"
                placeholder="Enter what the client is struggling with, their goals, and current setup..."
                className="min-h-[200px] text-sm"
                value={painPoints}
                onChange={(e) => setPainPoints(e.target.value)}
              />
            </div>

            <Button
              type="button"
              className="w-full"
              onClick={generateBlueprint}
              disabled={isGenerating || offeringsLoading}
            >
              {isGenerating ? (
                <>
                  <LoadingSpinner className="mr-2 h-4 w-4" />
                  Architecting Solution...
                </>
              ) : (
                <>
                  <Sparkles className="mr-2 h-4 w-4" />
                  Generate Blueprint
                </>
              )}
            </Button>
          </div>

          <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4 border border-gray-200 dark:border-gray-700 min-h-[400px]">
            {result ? (
              // A blueprint is generated markdown — headings, tables, fenced
              // config. A bare `<ReactMarkdown>` here had NO remark-gfm at all,
              // so a table printed its pipes verbatim, and `prose` styled
              // nothing (@tailwindcss/typography is not loaded in Hub).
              <div className="max-w-none text-sm">
                <MemoizedMarkdown id="solution-blueprint" content={result} />
              </div>
            ) : (
              <div className="h-full flex flex-col items-center justify-center text-center p-6 text-gray-500">
                <BrainCircuit className="h-12 w-12 mb-4 opacity-20" />
                <h3 className="font-medium text-gray-900 dark:text-gray-100 mb-1">No Blueprint Generated</h3>
                <p className="text-sm">
                  Enter client pain points on the left and click "Generate Blueprint" to see the recommended solution architecture.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </SimpleModal>
  );
}
